type EasingFunction = (p: number) => number;

type BaseEasingFunctionName =
  | 'Quad'
  | 'Cubic'
  | 'Quart'
  | 'Quint'
  | 'Expo'
  | 'Sine'
  | 'Circ'
  | 'Elastic'
  | 'Back'
  | 'Bounce';

type EasingFunctionName =
  | 'linear'
  | `easeIn${BaseEasingFunctionName}`
  | `easeOut${BaseEasingFunctionName}`
  | `easeInOut${BaseEasingFunctionName}`;

type EasingFunctions = {
  [key in EasingFunctionName]: EasingFunction;
};

const createEasings = (): EasingFunctions => {
  const easeInFunctions: Record<BaseEasingFunctionName, EasingFunction> = {
    Quad: (p) => p ** 2,
    Cubic: (p) => p ** 3,
    Quart: (p) => p ** 4,
    Quint: (p) => p ** 5,
    Expo: (p) => p ** 6,
    Sine: (p) => 1 - Math.cos((p * Math.PI) / 2),
    Circ: (p) => 1 - Math.sqrt(1 - p * p),
    Elastic: (p) =>
      p === 0 || p === 1
        ? p
        : -(2 ** (8 * (p - 1))) *
          Math.sin((((p - 1) * 80 - 7.5) * Math.PI) / 15),
    Back: (p) => p * p * (3 * p - 2),
    Bounce: (p) => {
      let bounce = 4;
      let pow2: number;
      do {
        bounce--;
        pow2 = 2 ** bounce;
      } while (p < (pow2 - 1) / 11);
      return 1 / 4 ** (3 - bounce) - 7.5625 * ((pow2 * 3 - 2) / 22 - p) ** 2;
    },
  };

  const entries: [EasingFunctionName, EasingFunction][] = [
    ['linear', (p) => p],
    ...Object.entries(easeInFunctions).flatMap(([name, easeIn]) => {
      const base = name as BaseEasingFunctionName;

      const easeOut: EasingFunction = (p) => 1 - easeIn(1 - p);
      const easeInOut: EasingFunction = (p) =>
        p < 0.5 ? easeIn(p * 2) / 2 : 1 - easeIn(2 - p * 2) / 2;

      return [
        [`easeIn${base}`, easeIn],
        [`easeOut${base}`, easeOut],
        [`easeInOut${base}`, easeInOut],
      ] as [EasingFunctionName, EasingFunction][];
    }),
  ];

  return Object.fromEntries(entries) as EasingFunctions;
};

export const easingFunctions = createEasings();
